import React, { useCallback, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useEffectOnce } from 'react-use';
import { Close, Logout } from '@mui/icons-material';
import {
  Box,
  Button,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { ArrowBackIcon } from '~assets/icons';
import {
  ConfirmationDialog,
  FullHeightSlideDialog,
  LoadingButton,
  NavBar,
} from '~components/atoms';
import { useModal } from '~hooks/useModal';
import {
  CameraPermissionProvider,
  LOCAL_CONTACT_SKIPPED_STEP,
  LOCAL_FORM_VALUES,
  LOCAL_IS_RELOCATION_FLOW,
  LOCAL_PINNING_STEP,
  LOCAL_PLACEMENT_LIST,
  LoggedInUserInfoProvider,
  StartAndFindVenueProvider,
  useAuth,
  useVenuePlacementCreationContext,
  VenuePlacementCreationProvider,
} from '~providers';
import {
  PINNING_STEP,
  PinningStepProvider,
  usePinningStepContext,
} from '~providers/PinningStepProvider';
import {
  FormCreateProvider,
  IFormCreationValues,
  useFormCreateContext,
} from '~providers/useForm';

export const MainLayout: React.FC = () => {
  return (
    <LoggedInUserInfoProvider>
      <CameraPermissionProvider>
        <PinningStepProvider>
          <StartAndFindVenueProvider>
            <VenuePlacementCreationProvider>
              <FormCreateProvider>
                <MainLayoutView />
              </FormCreateProvider>
            </VenuePlacementCreationProvider>
          </StartAndFindVenueProvider>
        </PinningStepProvider>
      </CameraPermissionProvider>
    </LoggedInUserInfoProvider>
  );
};

const MainLayoutView: React.FC = () => {
  const {
    getTitleOfCurrentStep,
    goToPreviousStep,
    pinningStep,
    requiredStepsInPinning,
    handleSetPinningStep,
    handleSetSelectedPlacementId,
    setIsRelocationFlow,
    setIsUpdating,
    handleSetContactSkippedStep,
  } = usePinningStepContext();

  const { handleReset, handleSetPlacementList } =
    useVenuePlacementCreationContext();

  const { handleSetInitialValues } = useFormCreateContext();

  const {
    isOpen: isOpenLogoutModal,
    openModal: openLogoutModal,
    closeModal: closeLogoutModal,
  } = useModal();

  const {
    isOpen: isOpenDeletePinningFormModal,
    openModal: openDeletePinningFormModal,
    closeModal: closeDeletePinningFormModal,
  } = useModal();

  const {
    isOpen: isOpenRollbackModal,
    openModal: openRollbackModal,
    closeModal: closeRollbackModal,
  } = useModal();

  const handleBackStep = useCallback(() => {
    if (pinningStep === PINNING_STEP.CREATE_VENUE_ADD_NAME) {
      handleReset(); // Add handleReset here to avoid unexpected reset state data
    }
    goToPreviousStep();
  }, [goToPreviousStep, handleReset, pinningStep]);

  const handleAcceptRollback = useCallback(() => {
    const savedLatestFormValues = localStorage.getItem(LOCAL_FORM_VALUES);
    if (!savedLatestFormValues) return;

    // Load saved latest form values
    const savedParsedLatestFormValues: IFormCreationValues = JSON.parse(
      savedLatestFormValues,
    );
    handleSetInitialValues(savedParsedLatestFormValues);

    // Load saved latest placement id of `drop station` request
    const dropStationPlacementId =
      savedParsedLatestFormValues.placement.placementId;
    dropStationPlacementId &&
      handleSetSelectedPlacementId(dropStationPlacementId);

    // Load saved list of requests (either `placement create` or `drop station` request)
    const savedPlacementListValues = localStorage.getItem(LOCAL_PLACEMENT_LIST);
    if (savedPlacementListValues) {
      const parsedList = JSON.parse(savedPlacementListValues);
      handleSetPlacementList(parsedList);
      // Restored list means user is re-editing existing placements, not adding new ones
      if (parsedList.length > 0) setIsUpdating(true);
    }

    // Load saved latest pinning step
    const localPinningStep = localStorage.getItem(LOCAL_PINNING_STEP);
    handleSetPinningStep(localPinningStep as PINNING_STEP);

    // Restore relocation flow flag
    const localIsRelocationFlow = localStorage.getItem(
      LOCAL_IS_RELOCATION_FLOW,
    );
    setIsRelocationFlow(localIsRelocationFlow === 'true');

    // Restore the skipped venue contact
    const localContactSkippedStep = localStorage.getItem(
      LOCAL_CONTACT_SKIPPED_STEP,
    ) as PINNING_STEP | null;
    handleSetContactSkippedStep(localContactSkippedStep ?? undefined);

    closeRollbackModal();
  }, [
    closeRollbackModal,
    handleSetContactSkippedStep,
    handleSetInitialValues,
    handleSetPinningStep,
    handleSetPlacementList,
    handleSetSelectedPlacementId,
    setIsRelocationFlow,
    setIsUpdating,
  ]);

  const handleDiscardRollback = () => {
    handleReset();
    closeRollbackModal();
  };

  const handleCloseLogoutModal: DialogProps['onClose'] = (event, reason) => {
    if (reason && reason === 'backdropClick') return;
    closeLogoutModal();
  };

  const handleCloseDeletePinningFormModal: DialogProps['onClose'] = (
    event,
    reason,
  ) => {
    if (reason && reason === 'backdropClick') return;
    closeDeletePinningFormModal();
  };

  const handleDeletePinningForm = useCallback(() => {
    closeDeletePinningFormModal();
    handleReset();
    handleSetPinningStep(PINNING_STEP.START);
  }, [closeDeletePinningFormModal, handleReset, handleSetPinningStep]);

  const { signOut } = useAuth();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const handleSignOut = () => {
    setIsSigningOut(true);
    signOut();
    setIsSigningOut(false);
  };

  const isAtHomePage = pinningStep === PINNING_STEP.START;
  const isUserPinning =
    requiredStepsInPinning.includes(pinningStep) ||
    pinningStep === PINNING_STEP.CONFIRM_VENUE ||
    pinningStep === PINNING_STEP.ASSIGN_STATION_SELECT_EXISTING_PLACEMENT ||
    pinningStep === PINNING_STEP.ASSIGN_STATION_SELECT_ACTION ||
    pinningStep === PINNING_STEP.CONFIRM_RELOCATION;

  useEffectOnce(() => {
    const localPinningStep = localStorage.getItem(LOCAL_PINNING_STEP);
    // Condition open modal at login
    if (
      localPinningStep &&
      localPinningStep !== 'null' &&
      localPinningStep !== PINNING_STEP.START
    ) {
      openRollbackModal();
    }
  });

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100vw',
          height: '100dvh',
          margin: 'auto',
        }}
      >
        {/* Navbar */}
        <NavBar
          LeftComponent={
            isAtHomePage ||
            pinningStep === PINNING_STEP.CONFIRM_VENUE ? null : (
              <IconButton size="large" onClick={handleBackStep}>
                <ArrowBackIcon />
              </IconButton>
            )
          }
          RightComponent={
            // Allow logout in homepage
            isAtHomePage ? (
              <IconButton size="large" onClick={openLogoutModal}>
                <Logout sx={{ color: 'text.charcoal' }} />
              </IconButton>
            ) : // Allow exit pinning form
            isUserPinning ? (
              <IconButton size="large" onClick={openDeletePinningFormModal}>
                <Close sx={{ color: 'text.charcoal' }} />
              </IconButton>
            ) : null
          }
          MiddleComponent={
            getTitleOfCurrentStep() ? (
              <Typography variant="h6" color="text.dark">
                {getTitleOfCurrentStep()}
              </Typography>
            ) : undefined
          }
        />

        {/* Outlet display */}
        <Stack
          overflow={'auto'}
          sx={{
            justifyContent: 'space-between',
            flex: 1,
            marginTop: '64px',
          }}
        >
          <Outlet />
        </Stack>
      </Box>
      {/* LOGOUT DIALOG */}
      <FullHeightSlideDialog
        open={isOpenLogoutModal}
        onClose={handleCloseLogoutModal}
      >
        <DialogTitle>
          <Typography variant="body2" fontWeight={400}>
            Are you sure you want to log out?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap={2}
          >
            <LoadingButton isLoading={isSigningOut} onClick={handleSignOut}>
              <Typography variant="body2" fontWeight={500}>
                Log out
              </Typography>
            </LoadingButton>
            <Button
              fullWidth
              disabled={isSigningOut}
              variant="outlined"
              onClick={(event) =>
                handleCloseLogoutModal(event, 'escapeKeyDown')
              }
              sx={{
                borderRadius: '100px',
                borderColor: 'text.charcoal',
              }}
            >
              <Typography
                variant="body2"
                fontWeight={500}
                color="text.charcoal"
              >
                Cancel
              </Typography>
            </Button>
          </Box>
        </DialogContent>
      </FullHeightSlideDialog>
      {/* DELETE PINNING FORM CONFIRMATION DIALOG */}
      <ConfirmationDialog
        open={isOpenDeletePinningFormModal}
        onClose={handleCloseDeletePinningFormModal}
        title="Discard changes?"
        content="Any unsaved changes will be lost. Are you sure you want to discard them and exit?"
        btnDeleteText="Discard"
        onDelete={handleDeletePinningForm}
        onCancel={closeDeletePinningFormModal}
      />
      <ConfirmationDialog
        open={isOpenRollbackModal}
        onClose={handleDiscardRollback}
        title="Resume your previous work?"
        content="Do you want to restore your last session's information?"
        btnDeleteText="Discard"
        onDelete={handleDiscardRollback}
        onAccept={handleAcceptRollback}
      />
    </>
  );
};
